<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div class="small">
        <p>
          FINRA Rule 3210 requires a registered representative to receive prior
          written consent from the member firm with which they are registered
          (the employing member) before opening any account, in which securities
          transactions can be effected and in which the registered person has a
          beneficial interest, at any other member firm (the executing member)
          or other financial institution. The registered representative must
          also notify in writing the executing member, or other financial
          institution, of their association with the employing member. In turn,
          the executing member is required, upon written request by the
          employing member, to transmit duplicate copies of confirmations and
          statements to the registered representative's employer for their
          account(s) including any account they have a beneficial interest in. A
          registered representative is presumed to have a beneficial interest in
          any account that is held by:
        </p>
        <p>
          <ol>
            <li>
              the spouse of the registered representative;
            </li>
            <li>
              a child of the registered representative or of the
              registered representative's spouse; provided that the child resides in
              the same household as or is financially dependent upon the associated
              person;
            </li>
            <li>
              other related individuals over whose account the
              registered representative has control; or
            </li>
            <li>
              other individual over
              whose account the registered representative has control and to whose
              financial support the registered representative materially contributes
              (FINRA Rule 3210.02).
            </li>
          </ol>
        </p>
      </div>
    </help-drawer>
    <v-form name="CompanyOfficial" class="form" data-test-id="beneficial-interest-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.BENEFICIAL_INTEREST.id"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="beneficial-interest-header">
              <span class="headerlight">
                Are <strong>you</strong>, or
                <strong> anyone with beneficial interest </strong> in this
                account, <strong>employed by</strong> or
                <strong>associated with</strong> any of the following?
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
            <div class="list-container" data-test-id="list-container">
              <ul role="list">
                <li class="list-item">Registered Broker-Dealer</li>
                <li class="list-item">FINRA</li>
                <li class="list-item">Stock Exchange</li>
              </ul>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving your information...</p>
        </div>
        
        <!-- Show data submitted message when not syncing and completed -->
        <data-submitted-message 
          v-if="isBeneficialInterestComplete && formHidden && !isSyncing" 
          :submitted="isBeneficialInterestComplete" 
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />

        <div v-if="!formHidden && !isSyncing">
          <v-row class="justify-center">
            <v-col class="step-form-container radio-container mb-4">
              <v-radio-group 
                v-model="beneficialInterest" 
                :error-messages="beneficialInterestErrors" 
                hide-details="auto"
                @change="$v.beneficialInterest.$touch()"
                data-test-id="beneficial-interest-radio-group"
              >
                <v-radio 
                  :ripple="false" 
                  class="v-faux-radio mb-4" 
                  label="Yes" 
                  :value="true"
                  data-test-id="beneficial-interest-yes"
                ></v-radio>
                <div class="expander" v-show="beneficialInterest">
                  <label for="companyName" data-test-id="company-name-label">Company Name</label>
                  <v-text-field 
                    outlined 
                    type="text" 
                    maxlength="256" 
                    name="companyName" 
                    v-model.trim="companyName"
                    :backgroundColor="inputBackgroundColor" 
                    @input="$v.companyName.$touch()"
                    @blur="$v.companyName.$touch()" 
                    :error-messages="companyNameErrors" 
                    hide-details="auto"
                    data-test-id="company-name-input"
                  />
                  <div class="finra-text" data-test-id="finra-text">
                    You will receive an email requesting a letter from your 
                    employer or affiliated broker-dealer approving the 
                    establishment of your account. This is required when 
                    submitting your application.
                  </div>
                </div>
                <v-radio 
                  :ripple="false" 
                  class="v-faux-radio" 
                  label="No" 
                  :value="false"
                  data-test-id="beneficial-interest-no"
                ></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>

          <div class="skip-for-now">
            <a @click="skip()" data-test-id="skip-button">Skip for now</a>
          </div>
        </div>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="beneficial-interest-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from "@/components/client/DataSubmittedMessage";
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { MENUS } from '@/common/constants';
import { required } from 'vuelidate/lib/validators';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';
import { requiredIf } from 'vuelidate/lib/validators';

export default {
  name: 'BeneficialInterest',
  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],

  data: () => ({
    beneficialInterest: null,
    companyName: null,
    letter: null,
    MENUS,
  }),

  validations() {
    return {
      beneficialInterest: {
        required,
      },
      companyName: {
        required: requiredIf(() => this.beneficialInterest),
      },
    };
  },

  computed: {
    // Is beneficial interest complete
    isBeneficialInterestComplete() {
      return this.isPageComplete(MENUS.CLIENT.BENEFICIAL_INTEREST.id);
    },
    
    // Check if the page is currently syncing
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.BENEFICIAL_INTEREST.id);
      return page && page.status === 'IN_PROGRESS';
    },
    
    helpTitle() {
      return 'What is a beneficial interest?';
    },

    // Dynamic  errors */
    beneficialInterestErrors() {
      const errors = [];
      if (!this.$v.beneficialInterest.$dirty) return errors;
      !this.$v.beneficialInterest.required &&
        errors.push('An answer is required');
      return errors;
    },

    companyNameErrors() {
      const errors = [];
      if (!this.$v.companyName.$dirty) return errors;
      !this.$v.companyName.required &&
        errors.push('Company Name is required');
      return errors;
    }
  },

  methods: {
    /* Nav to Next Page in flow */
    next(route = null) {      
      const to = route || this.pages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      const data = this.beneficialInterest
        ? {
          companyName: this.companyName,
        }
        : {};

      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(this.pages[this.pageIndex]);

      this.completeStep(this.pages[this.pageIndex], data)
        .then(() => {
          // Set formHidden to true after successful completion
          this.formHidden = true;
          this.goNext(to);
        })
        .catch(() => {});
    },

    /* Nav to Previous Page in flow */
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    /* Skip to next Page in flow */
    skip() {
      // Fetch updated interview progress before navigation
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },
    
    // Toggle form state with validation reset
    toggleFormState(hide) {
      this.formHidden = hide;
      
      // Reset form validation
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset();
        }
      });
    },
    
    // Update formHidden state when sync status changes
    handleSyncStatusChange() {
      // If completed, hide the form
      if (this.isBeneficialInterestComplete) {
        this.formHidden = true;
      }
    }
  },
  
  watch: {
    // Watch for changes in the sync status
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        // Sync has completed, check if form should be hidden
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    // Watch for changes in the completion status
    isBeneficialInterestComplete(newVal) {
      if (newVal === true) {
        this.formHidden = true;
      }
    }
  },

  created() {
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex = this.pages.indexOf(MENUS.CLIENT.BENEFICIAL_INTEREST.id);
    console.log('pages are ', this.pages)
    // Initialize form data for current interview
    this.formHidden = this.isBeneficialInterestComplete;
  },
  
  mounted() {
    // Check form state on mount
    this.$nextTick(() => {
      // If beneficial interest is complete, hide the form
      if (this.isBeneficialInterestComplete) {
        this.formHidden = true;
      }
    });
    
    // Start polling for updates to keep synced state updated
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  }
};
</script>
<style lang="scss" scoped>
.list-container {
  margin: 0 auto;
  max-width: 400px;
}

.finra-text {
  margin-top: 1em;
  margin-bottom: 1em;
}

.expander {
  margin-bottom: 2em;
}

.syncing-message {
  text-align: center;
  margin: 20px 0;
  
  p {
    color: #666;
    font-style: italic;
  }
}
</style>
